import React from "react";
import { cn } from "../../utils/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    description?: string;
    error?: string;
    required?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    // Base input classes with Neubrutal-lite aesthetic
    const baseInputClasses = "flex h-12 w-full rounded-xl border-2 border-[#0C2C39] bg-white px-4 py-3 text-sm font-medium text-[#0C2C39] placeholder:text-[#0C2C39]/30 focus:outline-none focus:shadow-[2px_2px_0px_0px_#0C2C39] transition-all disabled:cursor-not-allowed disabled:opacity-50";

    // Checkbox-specific styles
    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-5 w-5 rounded-md border-2 border-[#0C2C39] bg-white text-teal-700 focus:ring-0 focus:shadow-[2px_2px_0px_0px_#0C2C39] transition-all disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-5 w-5 rounded-full border-2 border-[#0C2C39] bg-white text-teal-700 focus:ring-0 focus:shadow-[2px_2px_0px_0px_#0C2C39] transition-all disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // For regular inputs with wrapper structure
    return (
        <div className="space-y-2.5">
            {label && (
                <div className="flex justify-between items-end mb-1">
                    <label
                        htmlFor={inputId}
                        className={cn(
                            "text-[10px] font-black  tracking-[0.2em] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                            error ? "text-red-700" : "text-[#0C2C39]/40"
                        )}
                    >
                        {label}
                        {required && <span className="text-red-700 ml-1">*</span>}
                    </label>
                    {description && !error && (
                        <span className="text-[9px] font-black  tracking-widest text-[#0C2C39]/20">
                            {description}
                        </span>
                    )}
                </div>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-red-700 focus:shadow-[2px_2px_0px_0px_rgba(185,28,28,1)] text-red-700",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />

            {error && (
                <p className="text-[10px] font-black  tracking-widest text-red-700">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;